import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, where, writeBatch, doc, orderBy, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { Container, Card, Spinner, Alert, Table, Form, Button, Tabs, Tab, Row, Col, DropdownButton, Dropdown, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Papa from 'papaparse';
import { convert } from 'encoding-japanese';

// --- IndexedDB & Permission Helper Functions ---
const DB_NAME = 'OrderSystemDB';
const STORE_NAME = 'settings';
const KEY_DIR_HANDLE = 'directoryHandle';

const getDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = (event) => resolve(event.target.result);
    request.onerror = (event) => reject(event.target.error);
  });
};

const saveDirectoryHandleToDB = async (handle) => {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(handle, KEY_DIR_HANDLE);
    request.onsuccess = () => resolve();
    request.onerror = (event) => reject(event.target.error);
  });
};

const getDirectoryHandleFromDB = async () => {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(KEY_DIR_HANDLE);
    request.onsuccess = (event) => resolve(event.target.result);
    request.onerror = (event) => reject(event.target.error);
  });
};

const verifyPermission = async (fileHandle, readWrite) => {
  const options = {};
  if (readWrite) {
    options.mode = 'readwrite';
  }
  if ((await fileHandle.queryPermission(options)) === 'granted') {
    return true;
  }
  if ((await fileHandle.requestPermission(options)) === 'granted') {
    return true;
  }
  return false;
};

// --- Standard Helper Functions ---

const addOneDayToDateString = (dateStr) => {
  if (!dateStr) return "";
  
  let date;
  let hasHyphen = dateStr.includes('-');
  let hasSlash = dateStr.includes('/');

  if (!hasHyphen && !hasSlash && dateStr.length === 8) {
    const y = dateStr.substring(0, 4);
    const m = dateStr.substring(4, 6);
    const d = dateStr.substring(6, 8);
    date = new Date(`${y}-${m}-${d}`);
  } else {
    date = new Date(dateStr);
  }

  if (isNaN(date.getTime())) return dateStr; 
  
  date.setDate(date.getDate() + 1);
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  if (hasHyphen) {
    return `${year}-${month}-${day}`;
  } else if (hasSlash) {
    return `${year}/${month}/${day}`;
  } else {
    return `${year}${month}${day}`;
  }
};

const formatDateToYYYYMMDD = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};

const formatDateToYYYYMMDDHHMM = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}${month}${day}${hours}${minutes}`;
};

const formatDisplayOrderNumber = (orderNumber) => {
  if (!orderNumber || orderNumber.length < 13) return orderNumber;
  const datePart = orderNumber.substring(0, 8);
  const dailyPart = orderNumber.substring(8, 11);
  const destPart = orderNumber.substring(11);
  return `${datePart}-${dailyPart}-${destPart}`;
};

const getTrackingUrl = (carrier, trackingNumber) => {
  if (!carrier || !trackingNumber) return null;
  const encodedTrackingNumber = encodeURIComponent(trackingNumber);
  switch (carrier) {
    case 'yupri':
      return `https://track.post.japanpost.jp/services/sp/srv/search/?requestNo1=${encodedTrackingNumber}`;
    case 'istar':
      return `https://corp.fukutsu.co.jp/situation/tracking_no_search?tracking_no=${encodedTrackingNumber}`;
    default:
      return null;
  }
};

function OrderProcessing() {
  const [orders, setOrders] = useState([]);
  const [usersMap, setUsersMap] = useState(new Map());
  const [companiesMap, setCompaniesMap] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  
  const [selectedDestinations, setSelectedDestinations] = useState(new Set());
  const [orderCsvFormats, setOrderCsvFormats] = useState({});

  const [directoryHandle, setDirectoryHandle] = useState(null);

  useEffect(() => {
    const loadHandle = async () => {
      try {
        const savedHandle = await getDirectoryHandleFromDB();
        if (savedHandle) {
          setDirectoryHandle(savedHandle);
        }
      } catch (err) {
        console.error("保存されたフォルダ設定の読み込みに失敗しました:", err);
      }
    };
    loadHandle();
  }, []);

  useEffect(() => {
    document.title = '受注処理 | My Order System';
    setLoading(true);

    const fetchInitialData = async () => {
      try {
        const usersQuery = getDocs(collection(db, 'users'));
        const companiesQuery = getDocs(collection(db, 'companies'));
        const [usersSnapshot, companiesSnapshot] = await Promise.all([usersQuery, companiesQuery]);
        const usersDataMap = new Map();
        usersSnapshot.forEach(doc => usersDataMap.set(doc.id, doc.data()));
        setUsersMap(usersDataMap);
        const companiesDataMap = new Map();
        companiesSnapshot.forEach(doc => companiesDataMap.set(doc.id, doc.data()));
        setCompaniesMap(companiesDataMap);
      } catch (err) {
        setError('ユーザー・会社情報の取得に失敗しました。');
        console.error(err);
      }
    };

    fetchInitialData();

    const ordersCollectionRef = collection(db, 'orders');
    const q = query(ordersCollectionRef, orderBy('orderedAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const fetchedOrders = querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      setOrders(fetchedOrders);
      setLoading(false);
    }, (err) => {
      setError('注文データの監視に失敗しました。');
      console.error(err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const flattenOrdersByDestination = useCallback((orderList) => {
    return orderList.flatMap(order => 
      order.shippingDestinations.map(dest => ({
        ...order,
        dest,
      }))
    );
  }, []);
  
  const csvPendingDestinations = useMemo(() => {
    const filteredOrders = orders.filter(o => o.status === 'processed' && !o.csvExportedAt);
    return flattenOrdersByDestination(filteredOrders);
  }, [orders, flattenOrdersByDestination]);

  // ★修正: direct (手渡し) 以外の出力済みデータをフィルタリング
  const csvExportedDestinations = useMemo(() => {
    const filteredOrders = orders.filter(o => (o.csvExportedAt || o.status === 'shipped') && o.shippingCarrier !== 'direct');
    return flattenOrdersByDestination(filteredOrders);
  }, [orders, flattenOrdersByDestination]);

  // ★追加: 手渡し (direct) 専用のデータセット
  const handDeliveredDestinations = useMemo(() => {
    const filteredOrders = orders.filter(o => o.shippingCarrier === 'direct');
    return flattenOrdersByDestination(filteredOrders);
  }, [orders, flattenOrdersByDestination]);


  const handleSelect = (e, destOrderNumber) => {
    setSelectedDestinations(prev => {
      const newSelected = new Set(prev);
      if (e.target.checked) {
        newSelected.add(destOrderNumber);
      } else {
        newSelected.delete(destOrderNumber);
      }
      return newSelected;
    });
  };

  const handleSelectAll = (e, destList) => {
    if (e.target.checked) {
      setSelectedDestinations(new Set(destList.map(d => d.dest.orderNumber)));
    } else {
      setSelectedDestinations(new Set());
    }
  };

  const handleFormatChange = (destOrderNumber, format) => {
    setOrderCsvFormats(prev => ({ ...prev, [destOrderNumber]: format }));
  };

  const handleBulkFormatChange = (selectedSet, format) => {
    if (selectedSet.size === 0) {
      alert('一括設定する注文を選択してください。');
      return;
    }
    const newFormats = { ...orderCsvFormats };
    selectedSet.forEach(destOrderNumber => {
      newFormats[destOrderNumber] = format;
    });
    setOrderCsvFormats(newFormats);
  };
  
  const handleSelectDirectory = async () => {
    if (!window.showDirectoryPicker) {
      alert('お使いのブラウザはこの機能に対応していません。');
      return;
    }
    try {
      const handle = await window.showDirectoryPicker();
      setDirectoryHandle(handle);
      await saveDirectoryHandleToDB(handle); 
    } catch (err) {
      console.error('フォルダの選択がキャンセルされたか、エラーが発生しました。', err);
    }
  };

  const saveFile = async (blob, filename) => {
    if (directoryHandle && typeof directoryHandle.getFileHandle === 'function') {
      try {
        const hasPermission = await verifyPermission(directoryHandle, true);
        if (!hasPermission) {
           throw new Error('フォルダへのアクセス権限が許可されませんでした。');
        }

        const fileHandle = await directoryHandle.getFileHandle(filename, { create: true });
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
        return;
      } catch (err) {
        console.error('指定フォルダへのファイル保存に失敗しました。', err);
        setError(`指定フォルダへの保存に失敗: ${err.message}。ダウンロードに切り替えます。`);
      }
    }

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const generateAndSaveCsv = async (data, headers, filename, encoding = 'utf8') => {
    const csvString = Papa.unparse({ fields: headers, data });
    let blob;
  
    if (encoding === 'sjis') {
      const unicodeArray = [];
      for (let i = 0; i < csvString.length; i++) {
        unicodeArray.push(csvString.charCodeAt(i));
      }
      const sjisUint8Array = new Uint8Array(convert(unicodeArray, {
        to: 'SJIS',
        from: 'UNICODE',
      }));
      blob = new Blob([sjisUint8Array], { type: 'text/csv;charset=shift_jis;' });
    } else {
      blob = new Blob([`\uFEFF${csvString}`], { type: 'text/csv;charset=utf-8;' });
    }
  
    await saveFile(blob, filename);
  };

  const generateFiles = async (destinationsToProcess, dateStr, dateTimeStr) => {
    const yupriDests = destinationsToProcess.filter(d => (orderCsvFormats[d.dest.orderNumber] || 'yupri') === 'yupri');
    const istarDests = destinationsToProcess.filter(d => orderCsvFormats[d.dest.orderNumber] === 'istar');
    
    if (yupriDests.length > 0) {
      yupriDests.sort((a, b) => a.dest.orderNumber.localeCompare(b.dest.orderNumber));

      const headers = [
        'お客様側管理番号',
        '送り状種別',
        'クール区別',
        '伝票番号',
        '出荷予定日',
        'お届け予定日',
        '時間帯',
        'お届け先コード',
        'お届け先電話番号',
        'お届け先電話番号枝番',
        'お届け先郵便番号',
        'お届け先住所',
        'お届け先住所２',
        'お届け先住所3',
        'お届け先名称',
        'お届け先名称（カナ）',
        'お届け先名称２',
        'お届け先名称２（カナ）',
        '敬称',
        'ご依頼主コード',
        'ご依頼主電話番号',
        'ご依頼主電話番号枝番',
        'ご依頼主郵便番号',
        'ご依頼主住所',
        'ご依頼主住所２',
        'ご依頼主名称',
        'ご依頼主名称（カナ）',
        '品名コード１',
        '品名１',
        '品名２',
        '荷扱い１',
        '荷扱い２',
        '記事',
        'コレクト代金引換額（税込）',
        '内消費税額等',
        '止置き',
        '営業所コード',
        '発行枚数',
        '個数口表示フラグ',
        '請求先顧客コード',
        '請求先分類コード',
        '運賃管理番号',
      ];
      const csvData = yupriDests.map(({ dest, ...order }) => ({
        'お客様側管理番号': dest.orderNumber,
        '送り状種別': 7,
        'クール区別': '',
        '伝票番号': '',
        '出荷予定日': order.estimatedShippingDate,
        'お届け予定日': '',
        '時間帯': '',
        'お届け先コード': '',
        'お届け先電話番号': dest.phoneNumber,
        'お届け先電話番号枝番': '', 
        'お届け先郵便番号': dest.postalCode,
        'お届け先住所': dest.address1 || '',
        'お届け先住所２': `${dest.address2 || ''}`,
        'お届け先住所3': `${dest.address3 || ''}`, 
        'お届け先名称': `${dest.shippingCompanyName || ''} ${dest.shippingBranchName || dest.branchName || ''} ${dest.shippingDepartmentName || dest.departmentName || ''}`.trim(),
        'お届け先名称（カナ）': '',
        'お届け先名称２': dest.recipientName,
        'お届け先名称２（カナ）': '',
        '敬称': '様',
        'ご依頼主コード': '', 
        'ご依頼主電話番号': '06-6453-5943',
        'ご依頼主電話番号枝番': '',
        'ご依頼主郵便番号': '5310061',
        'ご依頼主住所': '大阪府大阪市北区大淀中1-1-10',
        'ご依頼主住所２': '梅田スカイビルタワーウエスト16',
        'ご依頼主名称': '旭興西武サンプルセンター',
        'ご依頼主名称（カナ）': '', 
        '品名コード１': '',
        '品名１': `${dest.orderNumber} ${order.invoiceDetails}`,
        '品名２': "", 
        '荷扱い１': '',
        '荷扱い２': '',
        '記事': '',
        'コレクト代金引換額（税込）': '',
        '内消費税額等': '',
        '止置き': '',
        '営業所コード': '',
        '発行枚数': '',
        '個数口表示フラグ': '',
        '請求先顧客コード': '664535975',
        '請求先分類コード': '',
        '運賃管理番号': '1',
      }));
      await generateAndSaveCsv(csvData, headers, `yupri_shipping_data_${dateTimeStr}.csv`, 'utf8');
    }

    if (istarDests.length > 0) { 
      const headers = [ 
        "荷受人コード", "電話番号", "住居1", "住居2", "住居3", "名前", "名前2", "郵便番号", "特殊計", "着点コード", 
        "荷送人コード", "荷送担当者", "個数", "才数", "重量", "輸送商品1", "輸送商品2", "品名記事1", "品名記事2",
        "品名記事3", "品名記事4", "品名記事5", "品名記事6", "配達指定日", "必着区分", "お客様管理番号", 
        "元払区分", "保険金額", "出荷日付", "登録日付",
      ]; 
      const csvData = istarDests.map(({ dest, ...order }) => { 
        const productDetailsString = (order.products || []).map(p => `${p.partNumber} (数量:${p.quantity}) ${p.remarks || ''}`.trim()).join(' / '); 
        return { 
          "荷受人コード": "",
          "電話番号": (dest.phoneNumber || "").replace(/-/g, ''),
          "住居1": dest.address1 || "",
          "住居2": dest.address2 || "", 
          "住居3": dest.address3 || "",
          "名前": `${dest.shippingCompanyName || ''} ${dest.shippingBranchName || dest.branchName || ''} ${dest.shippingDepartmentName || dest.departmentName || ''}`.trim(),
          "名前2": dest.recipientName || "",
          "郵便番号": dest.postalCode || "", 
          "特殊計": "",
          "着点コード": "",
          "荷送人コード": "kyoku5943",
          "荷送担当者": "",
          "個数": "1", 
          "才数": "",
          "重量": "",
          "輸送商品1": "",
          "輸送商品２": "",
          "品名記事1": "",
          "品名記事2": order.invoiceDetails,
          "品名記事3": "",
          "品名記事4": "",
          "品名記事5": "",
          "品名記事6": "",
          "配達指定日": addOneDayToDateString(order.estimatedShippingDate),
          "必着区分": "",
          "お客様管理番号": dest.orderNumber || "", 
          "元払区分": "1",
          "保険金額": "",
          "出荷日付": dateStr,
          "登録日付": dateStr,
        }; 
      }); 
      
      await generateAndSaveCsv(csvData, headers, `fukuyama_shipping_data_${dateTimeStr}.csv`, 'sjis');
    }
  };

  const handleProcessOrders = async () => {
    if (selectedDestinations.size === 0) { alert('処理する注文を選択してください。'); return; }
    
    if (directoryHandle) {
      const hasPermission = await verifyPermission(directoryHandle, true);
      if (!hasPermission) {
        alert('保存先フォルダへのアクセス権限が許可されませんでした。再度「保存先フォルダを選択」から設定し直してください。');
        return;
      }
    } else if (window.showDirectoryPicker) {
      if (!window.confirm('保存先フォルダが選択されていません。PCのダウンロードフォルダに保存しますか？')) {
        return;
      }
    }

    setProcessing(true);
    setError('');
    try {
    const now = new Date();
    const dateStr = formatDateToYYYYMMDD(now);
    const dateTimeStr = formatDateToYYYYMMDDHHMM(now);
    const destinationsToProcess = csvPendingDestinations.filter(d => selectedDestinations.has(d.dest.orderNumber));

    await generateFiles(destinationsToProcess, dateStr, dateTimeStr);
    
    const batch = writeBatch(db);
    const parentOrderIds = new Set(destinationsToProcess.map(d => d.id));

    parentOrderIds.forEach(orderId => {
      const destForThisOrder = destinationsToProcess.find(d => d.id === orderId);
      const selectedFormat = orderCsvFormats[destForThisOrder.dest.orderNumber] || 'yupri';

      batch.update(doc(db, 'orders', orderId), { 
        csvExportedAt: serverTimestamp(),
        shippingCarrier: selectedFormat 
      });
    });
    await batch.commit();
      
      alert(`${selectedDestinations.size}件の送付先ファイルを出力しました。`);
      setSelectedDestinations(new Set());
    } catch (err) {
      setError('処理中にエラーが発生しました。');
      console.error(err);
    }
    setProcessing(false);
  };

  const handleReExport = async (selectedSet, allDestsInTab) => {
    if (selectedSet.size === 0) { alert('再出力する注文を選択してください。'); return; }

    if (directoryHandle) {
      const hasPermission = await verifyPermission(directoryHandle, true);
      if (!hasPermission) {
        alert('保存先フォルダへのアクセス権限が許可されませんでした。再度「保存先フォルダを選択」から設定し直してください。');
        return;
      }
    } else if (window.showDirectoryPicker) {
        if (!window.confirm('保存先フォルダが選択されていません。PCのダウンロードフォルダに保存しますか？')) {
            return;
        }
    }

    setProcessing(true);
    setError('');
    try {
      const now = new Date();
      const dateStr = formatDateToYYYYMMDD(now);
      const dateTimeStr = formatDateToYYYYMMDDHHMM(now);
      const destinationsToProcess = allDestsInTab.filter(d => selectedSet.has(d.dest.orderNumber));

      await generateFiles(destinationsToProcess, dateStr, dateTimeStr);
      alert(`${destinationsToProcess.length}件の送付先ファイルを再出力しました。`);
      setSelectedDestinations(new Set());
    } catch (err) {
      setError('ファイルの再出力に失敗しました。');
      console.error(err);
    }
    setProcessing(false);
  };

const handleMarkAsHandedOver = async (selectedSet, allDestsInTab) => {
  if (selectedSet.size === 0) { alert('処理する注文を選択してください。'); return; }
  if (!window.confirm('選択した注文を「受け渡し済み(出荷完了)」にしますか？\n※CSVファイルは出力されません。')) return;

  setProcessing(true);
  setError('');
  try {
    const batch = writeBatch(db);
    const destinationsToProcess = allDestsInTab.filter(d => selectedSet.has(d.dest.orderNumber));
    const parentOrderIds = new Set(destinationsToProcess.map(d => d.id));

    parentOrderIds.forEach(orderId => {
      batch.update(doc(db, 'orders', orderId), { 
        status: 'shipped', 
        shippingCarrier: 'direct',
        csvExportedAt: serverTimestamp() 
      });
    });
    await batch.commit();
    
    alert(`${parentOrderIds.size}件の注文を受け渡し済み（手渡し）にしました。`);
    setSelectedDestinations(new Set());
  } catch (err) {
    setError('処理中にエラーが発生しました。');
    console.error(err);
  }
  setProcessing(false);
};

  const handleMarkAsCancelled = async (selectedSet, allDestsInTab) => {
    if (selectedSet.size === 0) { alert('処理する注文を選択してください。'); return; }
    if (!window.confirm('選択した注文を「キャンセル」にしますか？\n※一覧から削除されます。')) return;

    setProcessing(true);
    setError('');
    try {
      const batch = writeBatch(db);
      const destinationsToProcess = allDestsInTab.filter(d => selectedSet.has(d.dest.orderNumber));
      const parentOrderIds = new Set(destinationsToProcess.map(d => d.id));

      parentOrderIds.forEach(orderId => {
        batch.update(doc(db, 'orders', orderId), { 
          status: 'cancelled' 
        });
      });
      await batch.commit();
      
      alert(`${parentOrderIds.size}件の注文をキャンセルしました。`);
      setSelectedDestinations(new Set());
    } catch (err) {
      setError('処理中にエラーが発生しました。');
      console.error(err);
    }
    setProcessing(false);
  };
  
  const renderOrderTable = (destList) => (
    <>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th><Form.Check type="checkbox" onChange={(e) => handleSelectAll(e, destList)} /></th>
            <th>発注番号</th><th>送り状番号</th><th>発注者</th>
            <th>送り状記載事項</th><th>送付先</th><th>出荷予定日</th><th>出力形式</th>
          </tr>
        </thead>
        <tbody>
          {destList.map(item => {
            const { dest, ...order } = item;
            const trackingUrl = getTrackingUrl(order.shippingCarrier, dest.trackingNumber);
            return (
              <tr key={dest.orderNumber}>
                <td><Form.Check type="checkbox" checked={selectedDestinations.has(dest.orderNumber)} onChange={(e) => handleSelect(e, dest.orderNumber)} /></td>
                <td>{formatDisplayOrderNumber(dest.orderNumber)}</td>
                <td>
                  {dest.trackingNumber ? (
                    <>
                      {trackingUrl ? ( <a href={trackingUrl} target="_blank" rel="noopener noreferrer">{dest.trackingNumber}</a> ) : ( <span>{dest.trackingNumber}</span> )}
                      <br/>
                    </>
                  ) : 'ー'}
                  <div className="mt-1">
                    {order.shippingCarrier === 'yupri' && <Badge bg="secondary">日本郵便</Badge>}
                    {order.shippingCarrier === 'istar' && <Badge bg="info">福山通運</Badge>}
                    {order.shippingCarrier === 'direct' && <Badge bg="success">手渡し</Badge>}
                  </div>
                </td>
                <td>{usersMap.get(order.orderedByUid)?.personName || '不明'}</td>
                <td>{order.invoiceDetails}</td>
                <td>{dest.recipientName}</td>
                <td>{order.estimatedShippingDate}</td>
                <td>
                  <Form.Select size="sm" value={orderCsvFormats[dest.orderNumber] || 'yupri'} onChange={(e) => handleFormatChange(dest.orderNumber, e.target.value)}>
                    <option value="yupri">ゆうプリR (csv)</option>
                    <option value="istar">福山通運 (csv)</option>
                  </Form.Select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      {!loading && destList.length === 0 && <p className="text-muted">対象の注文はありません。</p>}
    </>
  );

  return (
    <main>
      <Container className="my-4">
        <Card className="shadow-sm">
          <Card.Header as="h2" className="p-3">受注処理</Card.Header>
          <Card.Body className="p-4">
            {error && <Alert variant="danger">{error}</Alert>}
            <p className="mb-3 text-muted">自動印刷された注文の送り状データを出力します。新しい注文は自動で反映されます。</p>
            
            {window.showDirectoryPicker && (
              <Card border="info" className="mb-4">
                <Card.Body>
                  <Card.Title>ステップ1: 保存先フォルダの選択</Card.Title>
                  <Card.Text>
                    ファイルを出力する前に、保存先のフォルダを選択してください。一度設定すれば、<strong>ページを閉じても次回以降も設定が引き継がれます</strong>。<br/>
                    <small className="text-muted">※セキュリティの都合上、ブラウザを再起動した後の「初回出力時」にのみ、ブラウザ上部にファイルの変更を許可する確認ポップアップが表示されます。</small>
                  </Card.Text>
                  <Button variant="info" onClick={handleSelectDirectory}>保存先フォルダを選択</Button>
                  {directoryHandle && <p className="mt-2 mb-0 text-success">✔ 保存先フォルダ: 「{directoryHandle.name}」が設定されています。</p>}
                </Card.Body>
              </Card>
            )}

            <Tabs defaultActiveKey="pending" id="order-processing-tabs" className="mb-3" onSelect={() => setSelectedDestinations(new Set())}>
              <Tab eventKey="pending" title={`ファイル未出力の注文 (${csvPendingDestinations.length})`}>
                <Row className="d-flex justify-content-end align-items-center mb-3 g-2">
                  <Col md="auto">
                    <DropdownButton variant="outline-info" title={`選択した${selectedDestinations.size}件を一括設定`} size="sm" disabled={selectedDestinations.size === 0}>
                      <Dropdown.Item onClick={() => handleBulkFormatChange(selectedDestinations, 'yupri')}>ゆうプリR (csv)に設定</Dropdown.Item>
                      <Dropdown.Item onClick={() => handleBulkFormatChange(selectedDestinations, 'istar')}>福山通運 (csv)に設定</Dropdown.Item>
                    </DropdownButton>
                  </Col>
                  <Col md="auto">
                    <DropdownButton variant="outline-secondary" title="その他の操作" size="sm" disabled={selectedDestinations.size === 0}>
                      <Dropdown.Item onClick={() => handleMarkAsHandedOver(selectedDestinations, csvPendingDestinations)}>受け渡し済みにする (CSV不要)</Dropdown.Item>
                      <Dropdown.Item onClick={() => handleMarkAsCancelled(selectedDestinations, csvPendingDestinations)} className="text-danger">キャンセルにする</Dropdown.Item>
                    </DropdownButton>
                  </Col>
                  <Col md="auto">
                    <Button onClick={handleProcessOrders} disabled={selectedDestinations.size === 0 || processing}>
                      {processing ? <Spinner as="span" size="sm" /> : `選択した ${selectedDestinations.size} 件をファイル出力`}
                    </Button>
                  </Col>
                </Row>
                {loading ? <div className="text-center"><Spinner animation="border" /></div> : renderOrderTable(csvPendingDestinations)}
              </Tab>
              
              <Tab eventKey="exported" title={`ファイル出力済みの注文 (${csvExportedDestinations.length})`}>
                <Row className="d-flex justify-content-end align-items-center mb-3 g-2">
                    <Col md="auto" className="d-flex justify-content-end">
                        <DropdownButton variant="outline-info" title={`選択した${selectedDestinations.size}件を一括設定`} size="sm" disabled={selectedDestinations.size === 0} className="me-2">
                            <Dropdown.Item onClick={() => handleBulkFormatChange(selectedDestinations, 'yupri')}>ゆうプリR (csv)に設定</Dropdown.Item>
                            <Dropdown.Item onClick={() => handleBulkFormatChange(selectedDestinations, 'istar')}>福山通運 (csv)に設定</Dropdown.Item>
                        </DropdownButton>
                        <DropdownButton variant="outline-secondary" title="その他の操作" size="sm" disabled={selectedDestinations.size === 0} className="me-2">
                            <Dropdown.Item onClick={() => handleMarkAsHandedOver(selectedDestinations, csvExportedDestinations)}>受け渡し済みにする</Dropdown.Item>
                            <Dropdown.Item onClick={() => handleMarkAsCancelled(selectedDestinations, csvExportedDestinations)} className="text-danger">キャンセルにする</Dropdown.Item>
                        </DropdownButton>
                        <Button variant="outline-secondary" onClick={() => handleReExport(selectedDestinations, csvExportedDestinations)} disabled={selectedDestinations.size === 0 || processing}>
                            {processing ? <Spinner as="span" size="sm" /> : `選択した ${selectedDestinations.size} 件を再出力`}
                        </Button>
                    </Col>
                </Row>
                {loading ? <div className="text-center"><Spinner animation="border" /></div> : renderOrderTable(csvExportedDestinations)}
              </Tab>

              {/* ★追加: 手渡し専用タブ */}
              <Tab eventKey="direct" title={`手渡し済みの注文 (${handDeliveredDestinations.length})`}>
                <Row className="d-flex justify-content-end align-items-center mb-3 g-2">
                  <Col md="auto">
                    <DropdownButton variant="outline-secondary" title="その他の操作" size="sm" disabled={selectedDestinations.size === 0}>
                      <Dropdown.Item onClick={() => handleMarkAsCancelled(selectedDestinations, handDeliveredDestinations)} className="text-danger">キャンセルにする</Dropdown.Item>
                    </DropdownButton>
                  </Col>
                </Row>
                {loading ? <div className="text-center"><Spinner animation="border" /></div> : renderOrderTable(handDeliveredDestinations)}
              </Tab>
            </Tabs>

            <hr className="my-4" />
            <Link to="/shipper-menu">出荷者メニューに戻る</Link>
          </Card.Body>
        </Card>
      </Container>
    </main>
  );
}

export default OrderProcessing;